import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import { signinSchema } from "../schemas";
import { withRouter, Redirect } from "react-router-dom";
import { isAuthenticated } from "../auth";
import { login } from "../redux/actions/userActions";
import "./Signin.css";
import Popup from "../core/Popup";

const initialValues = {
  username: "",
  userPassword: "",
};

const Signin = ({ location, history }) => {
  const [openPopup, setOpenPopup] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState({
    username: false,
    userPassword: false,
  });
  
  const dispatch = useDispatch();
  
  const userLogin = useSelector((state) => state.userLogin);
  const { loading, error, userInfo } = userLogin;

  // Get redirect path from URL query params or default to dashboard
  const redirect = location.search
    ? location.search.split("=")[1]
    : "/dashboard";
    
  const { from } = location.state || { from: { pathname: redirect } };

  // Redirect if already logged in
  useEffect(() => {
    if (userInfo) {
      history.push(from.pathname);
    }
  }, [userInfo, history, from]);

  // Redirect authenticated users
  useEffect(() => {
    if (isAuthenticated()) {
      history.push("/dashboard");
    }
  }, [history]);

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldError,
  } = useFormik({
    initialValues,
    validationSchema: signinSchema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        const result = await dispatch(login(values));
        
        if (result?.errMsg) {
          switch (result.errMsg) {
            case "Wrong User Name!":
              setFieldError("username", result.errMsg);
              break;
            case "Wrong Password!":
              setFieldError("userPassword", result.errMsg);
              break;
            case "Inactive User!":
              setOpenPopup({
                ...openPopup,
                title: "Account Deactivated",
                subTitle: "Please contact the administrator to reactivate your account.",
                isOpen: true,
              });
              break;
            default:
              setFieldError("form", result.errMsg);
          }
        }
      } catch (error) {
        setFieldError("form", "An unexpected error occurred. Please try again.");
        console.error("Login error:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleForgotPassword = (e) => {
    e.preventDefault();
    setOpenPopup({
      ...openPopup,
      title: "Password Assistance",
      subTitle: "Please contact your system administrator for password reset.",
      isOpen: true,
    });
  };

  const handleFocus = (field) => {
    setIsFocused({ ...isFocused, [field]: true });
  };

  const handleBlurCustom = (e, field) => {
    setIsFocused({ ...isFocused, [field]: false });
    handleBlur(e);
  };

  // Show loading overlay during submission
  if (loading && isSubmitting) {
    return (
      <div className="signin-loading-overlay">
        <div className="signin-loading-content">
          <div className="spinner-container">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
          <p className="loading-text">Signing you in...</p>
        </div>
      </div>
    );
  }

  // Redirect if authenticated (for Redirect component)
  if (isAuthenticated()) {
    return <Redirect to="/dashboard" />;
  }

  return (
    <div className="signin-container">
      <div className="signin-background"></div>
      
      <div className="signin-card">
        <div className="signin-header">
          <div className="logo-container">
            <img
              src={`${process.env.PUBLIC_URL}/images/trade.png`}
              alt="Company Logo"
              className="logo"
            />
          </div>
          <h1 className="signin-title">Welcome Back</h1>
          <p className="signin-subtitle">Sign in to your account to continue</p>
        </div>

        <form className="signin-form" onSubmit={handleSubmit} noValidate>
          {/* Form Error */}
          {errors.form && (
            <div className="form-error-alert">
              <div className="error-icon">
                <i className="bi bi-exclamation-circle"></i>
              </div>
              <div className="error-message">{errors.form}</div>
              <button 
                type="button" 
                className="error-close"
                onClick={() => setFieldError("form", "")}
                aria-label="Close"
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
          )}

          {/* Redux error from API */}
          {error && (
            <div className="form-error-alert">
              <div className="error-icon">
                <i className="bi bi-exclamation-circle"></i>
              </div>
              <div className="error-message">{error}</div>
              <button 
                type="button" 
                className="error-close"
                onClick={() => {}}
                aria-label="Close"
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
          )}

          {/* Username Field */}
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              Username <span className="required-star">*</span>
            </label>
            <div className={`input-container ${isFocused.username ? 'focused' : ''} ${errors.username && touched.username ? 'error' : ''}`}>
              <div className="input-icon">
                <i className="bi bi-person"></i>
              </div>
              <input
                type="text"
                id="username"
                name="username"
                placeholder="Enter your username"
                value={values.username}
                onChange={handleChange}
                onFocus={() => handleFocus('username')}
                onBlur={(e) => handleBlurCustom(e, 'username')}
                disabled={isSubmitting}
                autoComplete="username"
                autoFocus
              />
            </div>
            {errors.username && touched.username && (
              <div className="field-error">
                <i className="bi bi-exclamation-circle"></i>
                {errors.username}
              </div>
            )}
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label htmlFor="userPassword" className="form-label">
              Password <span className="required-star">*</span>
            </label>
            <div className={`input-container ${isFocused.userPassword ? 'focused' : ''} ${errors.userPassword && touched.userPassword ? 'error' : ''}`}>
              <div className="input-icon">
                <i className="bi bi-lock"></i>
              </div>
              <input
                type={showPassword ? "text" : "password"}
                id="userPassword"
                name="userPassword"
                placeholder="Enter your password"
                value={values.userPassword}
                onChange={handleChange}
                onFocus={() => handleFocus('userPassword')}
                onBlur={(e) => handleBlurCustom(e, 'userPassword')}
                disabled={isSubmitting}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isSubmitting}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <i className={showPassword ? "bi bi-eye-slash" : "bi bi-eye"}></i>
              </button>
            </div>
            {errors.userPassword && touched.userPassword && (
              <div className="field-error">
                <i className="bi bi-exclamation-circle"></i>
                {errors.userPassword}
              </div>
            )}
          </div>

          {/* Forgot Password */}
          <div className="forgot-password-container">
            <button
              type="button"
              className="forgot-password-btn"
              onClick={handleForgotPassword}
              disabled={isSubmitting}
            >
              <i className="bi bi-key"></i>
              Forgot Password?
            </button>
          </div>

          {/* Submit Button */}
          <button
            className="submit-btn"
            type="submit"
            disabled={isSubmitting || !values.username || !values.userPassword}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-btn"></span>
                Signing In...
              </>
            ) : (
              <>
                <i className="bi bi-box-arrow-in-right"></i>
                Sign In
              </>
            )}
          </button>

          {/* Terms */}
          <div className="terms-container">
            <p className="terms-text">
              By signing in, you agree to our 
              <a href="/privacy" className="terms-link"> Privacy Policy</a> and 
              <a href="/terms" className="terms-link"> Terms of Service</a>.
            </p>
          </div>
        </form>

        {/* Footer */}
        {/* <div className="signin-footer">
          <p className="footer-text">
            Need help? <a href="/support" className="footer-link">Contact Support</a>
          </p>
          <p className="copyright">
            © {new Date().getFullYear()} Your Company. All rights reserved.
          </p>
        </div> */}
      </div>

      {/* Popup Component */}
      <Popup openPopup={openPopup} setOpenPopup={setOpenPopup} />
    </div>
  );
};

export default withRouter(Signin);